// Library Imports
import store from 'store';
import { message } from 'antd';
import { History } from 'history';
import { routes } from './constants';

// Local Imports
import { saveEmailDataService } from 'modules/Services/PublishService';
import { findEmailUsageInWholeWorkflow, findEmailsWithDesignNotReady, populateEmailLinks } from './utils';
import { generateDesignPreviewService, getDesignPreviewService } from 'modules/Services/DesignService';
import {
  ControlNameEnum,
  ControlTypeEnum,
  IShareProfileConfigurations,
  WFStatusEnum,
  IEmailData,
  IListData,
} from './interfaces';
import {
  emailsService,
  eventsService,
  listsService,
  updateWorkflowService,
  createWorkflow,
  getControls,
  getWorkflowDetailsService,
  getUserWorkflowsService,
  deleteWorkflowService,
  emailMappingService,
  getEmailsLinksService,
  getSharedProfileStaticDataService,
  resumeWorkflowService,
  cloneWorkflowService,
  getWorkflowEmailsAnalyticsService,
  getWorkflowEmailsGraphService,
} from 'modules/Services/AutomationServices';
import {
  AUTOMATION_EMAILS,
  AUTOMATION_EVENTS,
  AUTOMATION_DROP_ITEM,
  AUTOMATION_MODAL,
  CREATE_AUTOMATION,
  AUTOMATION_NODE,
  AUTOMATION_LISTS,
  AUTOMATION_NAME_UPDATE,
  AUTOMATION_CONTROLS,
  AUTOMATION_WORKFLOW,
  AUTOMATION_USER_WORKFLOWS,
  AUTOMATION_USER_WORKFLOWS_DELETE,
  AUTOMATION_WORKFLOW_STATUS_UPDATE,
  AUTOMATION_WORKFLOW_IF_ELSE_MODAL,
  AUTOMATION_WORKFLOW_CONDITION_EMAILS_LINKS,
  AUTOMATION_WORKFLOW_EMAIL_PREVIEW,
  AUTOMATION_SHARED_PROFILE_STATIC_DATA,
  AUTOMATION_WORKFLOW_TRIGGER_EMAIL,
  AUTOMATION_WORKFLOW_CLONE,
  AUTOMATION_LISTS_TRIGGER,
  AUTOMATION_WORKFLOW_TRIGGER_LIST,
  AUTOMATION_EMAILS_ANALYTICS_LIST,
  AUTOMATION_EMAILS_GRAPH_DATA,
} from '../../constants/types/automationTypes';
import { SelectOption } from 'modules/Campaigns/interfaces';
import { convertEmojisInStringToUnicode, getDateObject } from 'utils';
import { ReactNode } from 'react';

// Actions
export const addActionModal = (flag: boolean, id: string = '', tab: string = '', from?: string) => {
  return {
    type: AUTOMATION_MODAL.ADD_ACTION,
    payload: { flag, id, tab, from },
  };
};

export const deleteActionModal = (flag: boolean, id: string, index?: number, changeDeleteObject: boolean = true) => {
  return {
    type: AUTOMATION_MODAL.DELETE_ACTION,
    payload: { flag, id, index, changeDeleteObject },
  };
};

export const deleteConditionModal = (flag: boolean, id: string) => {
  return {
    type: AUTOMATION_MODAL.DELETE_CONDITION,
    payload: { flag, id },
  };
};

export const conditionConstraintModal = (flag: boolean) => {
  return {
    type: AUTOMATION_MODAL.CONDITION_CONSTRAINT,
    payload: { flag },
  };
};

export const setPublishModal = (flag: boolean) => {
  return {
    type: AUTOMATION_MODAL.SHOW_PUBLISH_MESSAGE,
    payload: { flag },
  };
};

export const setInactiveModal = (flag: boolean) => {
  return {
    type: AUTOMATION_MODAL.INACTIVE,
    payload: { flag },
  };
};

export const addNode = (payload: any) => {
  return {
    type: CREATE_AUTOMATION.ADD_NODE,
    payload,
  };
};

export const publishWorkflow = (onlySave?: boolean) => {
  const { automation } = store.getState();
  const workflow = automation?.workflow?.flow;
  const workflowId = automation?.workflow?.detail?.id;
  let data: { [key: string]: any } = {
    workflowId,
    workflow,
  };
  if (!onlySave) {
    data['status'] = WFStatusEnum.ACTIVE;
  }
  updatedWorkflowAPI(data);
};

export const eventsAPI = (searchTerm?: string) => {
  store.dispatch({
    type: AUTOMATION_EVENTS.LOAD,
  });
  eventsService(searchTerm)
    .then((res) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_EVENTS.LOAD_SUCCESS,
          events: res.data.data,
          isSearched: searchTerm,
        });
      } else {
        store.dispatch({ type: AUTOMATION_EVENTS.LOAD_FAILED });
      }
    })
    .catch((err) => {
      store.dispatch({ type: AUTOMATION_EVENTS.LOAD_FAILED });
    });
};

export const emailsAPI = (params: any) => {
  store.dispatch({
    type: AUTOMATION_EMAILS.LOAD,
    payload: { resetPage: params.offset === 0 },
  });
  if (params?.email_type === '2') delete params.email_type;
  emailsService(params)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_EMAILS.LOAD_SUCCESS,
          payload: { internalUser: res.data.data.internal_user },
          emails: res.data.data.emails,
          totalCount: res.data.data.total_count,
          paginatedCall: params.offset === 0 ? false : true,
        });
      } else {
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM,
          payload: { message: res.data.message, success: false },
        });
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM_LOADING,
        });
      }
    })
    .catch((err) => {
      console.error('emailAPI error: ', err);
      if (err?.response && !err?.response?.message?.includes('Operation canceled due to new request')) {
        store.dispatch({ type: AUTOMATION_EMAILS.LOAD_FAILED });
      }
    });
};

export const getSharedProfileData = () => {
  store.dispatch({
    type: AUTOMATION_SHARED_PROFILE_STATIC_DATA.LOAD,
  });
  getSharedProfileStaticDataService()
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_SHARED_PROFILE_STATIC_DATA.SUCCESS,
          payload: {
            shareProfileTemplatesConfigurationData: res.data.data.share_profiles.templates_configurations,
            shareProfileConfigurationsData: res.data.data.share_profiles.configurations,
            senderEmails: res.data.data.share_profiles.sender_emails,
            shareProfileTemplates: res.data.data.share_profiles.templates,
          },
        });
      } else {
        store.dispatch({ type: AUTOMATION_SHARED_PROFILE_STATIC_DATA.FAIL });
      }
    })
    .catch((err) => {
      console.error('emailAPI error: ', err);
      if (err?.response && !err?.response?.message?.includes('Operation canceled due to new request')) {
        store.dispatch({ type: AUTOMATION_SHARED_PROFILE_STATIC_DATA.FAIL });
      }
    });
};

export const resetWorkflowEmails = () => {
  store.dispatch({
    type: AUTOMATION_EMAILS.RESET,
  });
};

export const saveDropArea = (payload: any) => {
  return {
    type: AUTOMATION_DROP_ITEM.SAVE,
    payload,
  };
};

export const clearDropArea = () => {
  return {
    type: AUTOMATION_DROP_ITEM.CLEAR,
  };
};

export const deleteNode = (id: string, index: number) => {
  return {
    type: AUTOMATION_NODE.DELETE,
    payload: { id, index },
  };
};

export const copyNode = (payload: string) => {
  return {
    type: AUTOMATION_NODE.COPY,
    payload,
  };
};

export const clearCopyNode = () => {
  return {
    type: AUTOMATION_NODE.CLEAR_COPY,
  };
};

export const moveNode = (payload: string) => {
  return {
    type: AUTOMATION_NODE.MOVE,
    payload,
  };
};

export const editNode = (id: string, data: object, isShareProfileEditing = false) => {
  store.dispatch({
    type: AUTOMATION_WORKFLOW.EDITING_SHARE_PROFILE,
    payload: isShareProfileEditing,
  });
  return {
    type: AUTOMATION_NODE.EDIT,
    payload: { id, data },
  };
};

export const setNodeError = (id: string, isError: boolean) => {
  return {
    type: AUTOMATION_NODE.ERROR,
    payload: { id, isError },
  };
};

export const resetWorkflow = () => {
  return {
    type: AUTOMATION_WORKFLOW.RESET,
  };
};

export const addWFEmail = (payload: number) => {
  return {
    type: AUTOMATION_WORKFLOW.ADD_EMAIL,
    payload,
  };
};

export const removeWFEmail = (payload: number) => {
  return {
    type: AUTOMATION_WORKFLOW.REMOVE_EMAIL,
    payload,
  };
};

export const updateRecentlyPublished = (payload: boolean) => {
  return {
    type: AUTOMATION_WORKFLOW.UPDATE_RECENTLY_PUBLISHED,
    payload,
  };
};

export const listsAPI = (params: any) => {
  let searchTerm = params?.list_name;
  let reset = params ? params.reset : false;
  store.dispatch({
    type: AUTOMATION_LISTS.LOAD,
  });
  listsService(params)
    .then((res) => {
      if (res.data.success) {
        store.dispatch({
          type: searchTerm ? AUTOMATION_LISTS.SET_SEARCH_DATA : AUTOMATION_LISTS.LOAD_SUCCESS,
          lists: res.data.data,
          reset: reset,
          totalCount: res.data.total_count,
        });
      } else {
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM,
          payload: { message: res.data.message, success: false },
        });
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM_LOADING,
        });
      }
    })
    .catch((err) => {
      if (err?.response && !err?.response?.message?.includes('Operation canceled due to new request')) {
        store.dispatch({ type: AUTOMATION_LISTS.LOAD_FAILED });
      }
    });
};

export const resetWorkflowLists = () => {
  store.dispatch({ type: AUTOMATION_LISTS.RESET });
};

export const nameUpdateHandler = (payload: string, workflowId: number) => {
  const obj = { workflow_name: payload, workflow_id: workflowId };
  store.dispatch({
    type: AUTOMATION_NAME_UPDATE.LOAD,
  });
  updateWorkflowService(obj)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_NAME_UPDATE.SUCCESS,
          payload,
        });
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.UPDATE_USERS_WORKFLOW_NAME,
          payload: { name: payload, workflowId },
        });
      } else {
        store.dispatch({
          type: AUTOMATION_NAME_UPDATE.FAIL,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_NAME_UPDATE.FAIL,
      });
    });
};

export const getControlsAPI = () => {
  getControls()
    .then((res) => {
      const data = res.data.data;
      let lastItem = data.action.pop();
      let arr = [
        ...data.action,
        {
          id: 3,
          name: ControlNameEnum.IF_ELSE,
          title: ControlNameEnum.IF_ELSE,
          type: ControlTypeEnum.CONDITION,
          conditions: [...data.condition],
        },
        lastItem,
      ];
      store.dispatch({
        type: AUTOMATION_CONTROLS.SAVE,
        payload: arr,
      });
    })
    .catch((error) => {});
};

export const createWorkflowAPI = (emailId: number = 0, triggerId: number = 0, listId: number = 0, history: History) => {
  store.dispatch({
    type: CREATE_AUTOMATION.LOADING,
  });
  createWorkflow(emailId, triggerId, listId)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: CREATE_AUTOMATION.SAVE,
          payload: res.data.data,
        });
        history(`${routes.DETAIL}?workflow_id=${res.data.data.id}`);
      } else {
        store.dispatch({
          type: CREATE_AUTOMATION.FAIL,
        });
        console.log('error');
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: CREATE_AUTOMATION.FAIL,
      });
      console.log(err);
    });
};

export const updatedWorkflowAPI = (payload: any, isAutoSave?: boolean) => {
  const { workflowId, workflow, status, newlyAddedNodes, designReady } = payload;
  const stateWorkflowDetail = store.getState().automation.workflow.detail;
  const workflowSaveButtonDisabledState = store.getState().automation.workflow.saveButtonDisabled;
  const emailData = store.getState().editionData;
  const { isShareProfileEditing } = store.getState().automation.workflow;
  // enabling the "Save..." button if changes made
  if (workflowSaveButtonDisabledState) {
    updateSaveButtonState(false);
  }

  if (emailData.workflowId === workflowId && emailData.editionName) {
    const ids = findEmailUsageInWholeWorkflow(emailData.emailId);
    ids.forEach((id) => {
      if (
        workflow[id]?.detail?.controlName === ControlNameEnum.SEND_EMAIL &&
        workflow[id]?.detail?.emailName !== emailData.editionName
      ) {
        workflow[id].detail.emailName = emailData.editionName;
        workflow[id].detail.nodeTitle = emailData.editionName;
      }
      // Commenting bcz it updates the emailName on adding new Codition Node the first time after creating a new workflow
      // else if (workflow[id]?.detail.controlType === ControlTypeEnum.CONDITION) {
      // workflow[id]?.detail.conditions?.forEach((item: any) => {
      //   if (emailData.editionName !== item?.emailName) {
      //     item.emailName = emailData.editionName;
      //   }
      // });
      // }
    });
  }

  // checking if the designs of all the emails in the workflow are ready
  if (designReady) {
    const emailIds = findEmailsWithDesignNotReady();
    const uniqueEmailIds = [...new Set(emailIds)];
    if (uniqueEmailIds?.length) {
      uniqueEmailIds.forEach((id) => {
        if (workflow[id]?.detail?.emailName === emailData.editionName) {
          workflow[id].detail.designReady = true;
        }
      });
      // If only one email has design not ready then its design is ready in upper code
      allEmailsInWorkflowDesignReady(uniqueEmailIds?.length === 1);
    }
  }

  // newly added nodes handling
  if (newlyAddedNodes && newlyAddedNodes?.length !== 0 && !status) {
    workflow['newlyAddedNodes'] = newlyAddedNodes;
  } else {
    if (status === WFStatusEnum.INACTIVE && 'newlyAddedNodes' in stateWorkflowDetail) {
      store.dispatch({ type: AUTOMATION_WORKFLOW.EMPTY_NEWLY_ADDED_NODES });
    }
  }
  let obj = {
    workflow_id: workflowId,
    ...(workflow && { workflow: JSON.stringify(workflow) }),
    ...(status && { status }),
  };

  if (!isAutoSave) {
    store.dispatch({
      type: CREATE_AUTOMATION.LOADING,
    });
  }

  updateWorkflowService(obj)
    .then((res: any) => {
      if (res.data.success) {
        let designNotReady = res.data?.design_ready;
        let spData = res.data?.share_profile_email_data;
        store.dispatch({
          type: CREATE_AUTOMATION.UPDATE,
          payload: { status, designNotReady },
        });
        store.dispatch({ type: AUTOMATION_EMAILS_ANALYTICS_LIST.RESET_DATA });
        if (Object.keys(spData)?.length) {
          const itemData = store.getState().automation.workflow.flow[spData.node_id].detail;
          store.dispatch(
            editNode(spData.node_id, {
              ...itemData,
              emailId: spData.email_id,
            }),
          );
        }
        if (status)
          store.dispatch({
            type: AUTOMATION_USER_WORKFLOWS.UPDATE_USERS_WORKFLOW_STATUS,
            payload: { status, workflowId },
          });
        status === WFStatusEnum.ACTIVE && store.dispatch(updateRecentlyPublished(true));
      } else {
        store.dispatch({
          type: CREATE_AUTOMATION.FAIL,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: CREATE_AUTOMATION.FAIL,
      });
    })
    .finally(() => {
      store.dispatch({
        type: AUTOMATION_WORKFLOW.EDITING_SHARE_PROFILE,
        payload: false,
      });
      store.dispatch({
        type: AUTOMATION_WORKFLOW.RESET_EMAILS,
      });
    });

  !isShareProfileEditing && emailMappingAPI(workflowId);
};

export const resetSuccessMessage = () => {
  store.dispatch({
    type: CREATE_AUTOMATION.RESET_MESSAGE,
  });
};

export const getWorkflowDetails = (workflowId: number, callback?: any) => {
  store.dispatch({
    type: AUTOMATION_WORKFLOW.LOAD,
  });
  getWorkflowDetailsService(workflowId)
    .then((res: any) => {
      if (res?.data?.success) {
        store.dispatch({
          type: AUTOMATION_WORKFLOW.LOAD_SUCCESS,
          payload: res.data.data,
        });
        callback();
      } else {
        store.dispatch({
          type: AUTOMATION_WORKFLOW.LOAD_FAILED,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_WORKFLOW.LOAD_FAILED,
      });
      console.log(err);
    });
};

export const getUserWorkflows = (offset: number, limit: number, workflowName?: string, filterType?: string) => {
  store.dispatch({
    type: AUTOMATION_USER_WORKFLOWS.LOAD,
    payload: { offset },
  });
  getUserWorkflowsService(offset, limit, workflowName, filterType)
    .then((res: any) => {
      if (res.data.success) {
        if (Boolean(workflowName)) {
          store.dispatch({
            type: AUTOMATION_USER_WORKFLOWS.SET_SEARCH_TERM,
            payload: workflowName,
          });
        }
        if (Boolean(filterType)) {
          store.dispatch({
            type: AUTOMATION_USER_WORKFLOWS.SET_FILTER_TYPE,
            payload: filterType,
          });
        }
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.LOAD_SUCCESS,
          userWorkflows: res.data.data,
          isSearched: Boolean(workflowName) || Boolean(filterType),
          totalCount: res.data.total_count,
          totalActiveCount: res.data.total_active_count,
          paginatedCall: offset === 0 ? false : true,
          offset,
        });
      } else {
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM,
          payload: { message: res.data.message, success: false },
        });
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM_LOADING,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_USER_WORKFLOWS.LOAD_FAILED,
      });
      console.log(err);
    });
};

export const incrementTotalCount = () => ({
  type: AUTOMATION_USER_WORKFLOWS.INCREMENT_TOTAL_COUNT,
});

export const incrementActiveCount = () => ({
  type: AUTOMATION_USER_WORKFLOWS.INCREMENT_ACTIVE_COUNT,
});

export const decrementActiveCount = () => ({
  type: AUTOMATION_USER_WORKFLOWS.DECREMENT_ACTIVE_COUNT,
});

export const resetUserWorkflows = () => {
  store.dispatch({
    type: AUTOMATION_USER_WORKFLOWS.RESET,
  });
};

export const deleteWorkflow = (
  workflowIds: number[],
  isSearched: boolean,
  selectAll: boolean,
  excludedWorkflowIds: number[],
  searchTerm: string,
  filterType: string,
) => {
  store.dispatch({
    type: AUTOMATION_USER_WORKFLOWS_DELETE.LOAD,
  });

  deleteWorkflowService(workflowIds, selectAll, excludedWorkflowIds, searchTerm, filterType)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS_DELETE.LOAD_SUCCESS,
          payload: workflowIds,
          totalCount: res.data.total_count,
          isSearched: isSearched,
          searchTerm: searchTerm,
          filterType: filterType,
        });
      } else {
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS_DELETE.LOAD_FAILED,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_USER_WORKFLOWS_DELETE.LOAD_FAILED,
      });
      console.log(err);
    });
};

export const updateWorkflowErrorUpdate = () => {
  store.dispatch({
    type: AUTOMATION_WORKFLOW_STATUS_UPDATE.ERROR_UPDATE,
  });
};

export const updateWorkflowStatusAPI = (workflowId: number, workflowStatus: string) => {
  const updatedWorkflowObj = { workflow_id: workflowId, status: workflowStatus };
  store.dispatch({
    type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD,
  });
  updateWorkflowService(updatedWorkflowObj)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_SUCCESS,
          payload: { workflowId, workflowStatus },
        });
        if (workflowStatus === 'inactive') {
          store.dispatch(decrementActiveCount());
        } else {
          store.dispatch(incrementActiveCount());
        }
      } else {
        store.dispatch({
          type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_FAILED,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_FAILED,
      });
    });
};

export const emailMappingAPI = (workflowId: number) => {
  const { addEmails, removeEmails } = store.getState().automation.workflow;
  if (addEmails.length || removeEmails.length) {
    // if both the arrays are empty then API request should not go from the frontend
    const emailsObj = {
      workflow_id: workflowId,
      ...(addEmails.length && { add_emails: addEmails }),
      ...(removeEmails.length && { remove_emails: removeEmails }),
    };
    emailMappingService(emailsObj)
      .then((res: any) => {
        if (res.data.success) {
          store.dispatch({
            type: AUTOMATION_WORKFLOW.RESET_EMAILS,
          });
        }
      })
      .catch(() => {
        store.dispatch({
          type: AUTOMATION_WORKFLOW.LOAD_FAILED,
        });
      });
  }
};

export const updateToasterMessage = (message: string) => {
  return {
    type: CREATE_AUTOMATION.UPDATE_TOASTER_MESSAGE,
    payload: { message },
  };
};

export const setSelectedEmailAction = (emailId: number, emailTitle: string, campaignId: number) => {
  store.dispatch({
    type: AUTOMATION_EMAILS.SET_SELECTED_EMAIL_ID,
    payload: { emailId, emailTitle, campaignId },
  });
};

export const setSelectedlistIdsAction = (list_ids: number[]) => {
  store.dispatch({
    type: AUTOMATION_LISTS.SET_SELECTED_LIST_IDS,
    payload: { list_ids },
  });
};
export const setSelectedListsAction = (lists: IListData[]) => {
  store.dispatch({
    type: AUTOMATION_LISTS.SET_SELECTED_LISTS,
    payload: { lists },
  });
};

export const setSelectedIfElsesAction = (conditionId: number, conditionEmailId: number) => {
  store.dispatch({
    type: AUTOMATION_WORKFLOW_IF_ELSE_MODAL.SET_CONDITION_EMAIL,
    payload: { conditionId, conditionEmailId },
  });
};

export const updateErrorNodes = (value: string) => {
  return {
    type: AUTOMATION_WORKFLOW.UPDATE_ERROR_NODES_LIST,
    payload: value,
  };
};

export const updateErrorPublishNodes = (value: string) => {
  return {
    type: AUTOMATION_WORKFLOW.UPDATE_ERROR_PUBLISH_NODES_LIST,
    payload: value,
  };
};

export const updateFilterInStore = (value: string) => {
  store.dispatch({
    type: AUTOMATION_EMAILS.SET_FILTERS,
    payload: value,
  });
};

export const showUpdateButton = (value: boolean) => {
  store.dispatch({
    type: AUTOMATION_EMAILS.SET_SHOW_UPDATE,
    payload: value,
  });
};

export const updateErrorEmailDataInRedux = (
  values: Omit<IEmailData, 'campaignName' | 'title' | 'date' | 'thumbnail'>,
) => {
  store.dispatch({
    type: AUTOMATION_EMAILS.UPDATE_EMAIL_DATA,
    payload: values,
  });
};

export const saveEmailData = (values: Omit<IEmailData, 'campaignName' | 'title' | 'date' | 'thumbnail'>) => {
  let formData = new FormData();
  formData.append('campaign_id', values.campaignId.toString());
  formData.append('email_id', values.emailId.toString());
  formData.append('email_type', values.versionEmailType.toString());
  formData.append('design_ready', values.designReady ? 'true' : 'false');
  formData.append('sender_name', values.senderName);
  formData.append('sender_email', values.sendFrom);
  formData.append('sender_domain', values.sendFrom);
  formData.append('subject', convertEmojisInStringToUnicode(values.subject));
  if (values?.platform) formData.append('send_email_platform', values?.platform);
  return saveEmailDataService(formData);
};

export const shareProfileConfigurationModal = (isVisible: boolean) => {
  return {
    type: AUTOMATION_MODAL.SHARE_PROFILE_CONFIG,
    payload: { isVisible },
  };
};

export const saveShareProfileConfiguration = (configurations: IShareProfileConfigurations) => {
  return {
    type: AUTOMATION_WORKFLOW.SHARE_PROFILE_CONFIG_UPDATE,
    shareProfileConfigurations: configurations,
  };
};

export const getEmailList = (data: number[]) => {};

export const getEmailsLinks = (ids: string, nodeId: string, workflowId: number) => {
  store.dispatch({ type: AUTOMATION_WORKFLOW_CONDITION_EMAILS_LINKS.LOAD });
  getEmailsLinksService(ids, nodeId, workflowId)
    .then((res) => {
      if (res?.data?.success) {
        const result = populateEmailLinks(res.data.data);
        store.dispatch({
          type: AUTOMATION_WORKFLOW_CONDITION_EMAILS_LINKS.LOAD_SUCCESS,
          payload: result,
        });
      }
    })
    .catch((err) => {
      console.log('error: ', err);
    });
};

export const getEmailPreview = (emailId: number, campaignId: number) => {
  store.dispatch({
    type: AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD,
    selectedEmailId: emailId,
    selectedCampaignId: campaignId,
  });
  getDesignPreviewService(emailId, '', 0)
    .then((res) => {
      if (res?.data?.asset_name) {
        getEmailPreviewWithCamp(emailId, campaignId, res.data.preview_html);
        store.dispatch({
          type: AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD_SUCCESS,
          payload: {
            preview_html: res.data.preview_html,
            preview_text: '',
            default_preview_text: res.data.preview_text,
          },
          selectedEmailId: emailId,
        });
      } else {
        store.dispatch({ type: AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD_FAILED });
      }
    })
    .catch((err) => {
      console.log(err);
      if (err?.response && !err?.response?.message?.includes('Operation canceled due to new request')) {
        store.dispatch({ type: AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD_FAILED });
      }
    });
};

export const getEmailPreviewWithCamp = (emailId: number, campaignId: number, html: string) => {
  generateDesignPreviewService(campaignId, emailId, undefined, html, undefined, true)
    .then((res) => {
      console.log(res);
      if (res?.data?.preview_text) {
        store.dispatch({
          type: AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD_TEXT_PREVIEW_SUCCESS,
          payload: res.data.preview_text,
          selectedEmailId: emailId,
        });
      } else {
        store.dispatch({ type: AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD_FAILED });
      }
    })
    .catch((err) => {
      console.log(err);
      if (err?.response && !err?.response?.message?.includes('Operation canceled due to new request')) {
        store.dispatch({ type: AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD_FAILED });
      }
    });
};

export const resetSelectedPreviewEmailId = () => {
  store.dispatch({ type: AUTOMATION_WORKFLOW_EMAIL_PREVIEW.RESET_SELECTED_EMAIL_ID });
};

// Update trigger email in workflow
export const updateWorkflowTriggerEmail = (
  workflowId: number,
  emailId: number,
  emailName: string,
  campaignId: number,
) => {
  const updatedWorkflowObj = {
    workflow_id: workflowId,
    trigger_email_update: true,
    email_id: emailId,
  };
  const workflowSaveButtonDisabledState = store.getState().automation.workflow.saveButtonDisabled;
  if (workflowSaveButtonDisabledState) {
    // enabling the Save button if changes made
    updateSaveButtonState(false);
  }
  store.dispatch({
    type: AUTOMATION_WORKFLOW_TRIGGER_EMAIL.LOAD,
  });
  store.dispatch({ type: AUTOMATION_WORKFLOW_TRIGGER_EMAIL.UPDATE, payload: { emailId, emailName, campaignId } });
  updateWorkflowService(updatedWorkflowObj)
    .then((res: any) => {
      if (!res.data.success) {
        store.dispatch({
          type: AUTOMATION_WORKFLOW_TRIGGER_EMAIL.FAIL,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_WORKFLOW_TRIGGER_EMAIL.FAIL,
      });
    });
};

export const updateWorkflowTriggerList = (workflowId: number, listId: any, listName: string | ReactNode) => {
  const updatedWorkflowObj = {
    workflow_id: workflowId,
    trigger_list_id: listId,
  };
  const workflowSaveButtonDisabledState = store.getState().automation.workflow.saveButtonDisabled;
  if (workflowSaveButtonDisabledState) {
    // enabling the Save button if changes made
    updateSaveButtonState(false);
  }
  store.dispatch({
    type: AUTOMATION_WORKFLOW_TRIGGER_EMAIL.LOAD,
  });
  store.dispatch({ type: AUTOMATION_WORKFLOW_TRIGGER_LIST.UPDATE, payload: { listId, listName } });
  updateWorkflowService(updatedWorkflowObj)
    .then((res: any) => {
      if (!res.data.success) {
        store.dispatch({
          type: AUTOMATION_WORKFLOW_TRIGGER_EMAIL.FAIL,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_WORKFLOW_TRIGGER_EMAIL.FAIL,
      });
    });
};

export const resumeWorkflowAPI = (payload: any, isDetailPage?: boolean) => {
  const { workflowId, status } = payload;

  let obj = {
    workflow_id: workflowId,
    ...(status && { status }),
  };

  store.dispatch({
    type: CREATE_AUTOMATION.LOADING,
  });

  return resumeWorkflowService(obj)
    .then((res: any) => {
      if (res.data.success) {
        if (isDetailPage) {
          message.success({
            content: 'Your workflow has been reactivated',
          });
        }
        store.dispatch({
          type: CREATE_AUTOMATION.RESUME,
          payload: { status },
        });
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.UPDATE_USERS_WORKFLOW_STATUS,
          payload: { status, workflowId },
        });
      } else {
        if (isDetailPage) {
          message.error({
            content: 'Error in resuming the Workflow',
          });
        }
        store.dispatch({
          type: CREATE_AUTOMATION.FAIL,
        });
      }
      return res;
    })
    .catch((err: any) => {
      store.dispatch({
        type: CREATE_AUTOMATION.FAIL,
      });
      return err;
    });
};

export const selectedEmailOnTop = (data: object, emailId?: number) => {
  return {
    type: AUTOMATION_EMAILS.SELECTED_ON_TOP,
    payload: { emailId, data },
  };
};

export const cloneWorkflow = (workflowId: number, filterType: string) => {
  const obj = { workflow_id: workflowId };
  store.dispatch({
    type: AUTOMATION_WORKFLOW_CLONE.LOAD,
  });

  return cloneWorkflowService(obj)
    .then((res: any) => {
      if (res?.data?.success) {
        store.dispatch({
          type: AUTOMATION_WORKFLOW_CLONE.LOAD_SUCCESS,
          payload: { data: res.data.data, workflowId: res.data.data.id },
        });
        if (filterType === '' || filterType === 'draft') {
          store.dispatch(incrementTotalCount());
        }
      } else {
        store.dispatch({
          type: AUTOMATION_WORKFLOW_CLONE.LOAD_FAILED,
          payload: res.data.message,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_WORKFLOW_CLONE.LOAD_FAILED,
      });
      console.log(err);
    });
};

export const getWorkflowEmailsAnalytics = (workflowId: number, searchTerm: string) => {
  store.dispatch({ type: AUTOMATION_EMAILS_ANALYTICS_LIST.LOAD });
  getWorkflowEmailsAnalyticsService(workflowId, searchTerm)
    .then((res) => {
      if (res.data.success) {
        const { emails, total_count: emailsCount } = res.data.data;
        store.dispatch({ type: AUTOMATION_EMAILS_ANALYTICS_LIST.SET_DATA, payload: { emails, emailsCount } });
      }
    })
    .catch((err) => {
      console.log(err);
      store.dispatch({ type: AUTOMATION_EMAILS_ANALYTICS_LIST.RESET_DATA });
    });
};

export const getWorkflowEmailsGraphData = (data: any) => {
  store.dispatch({ type: AUTOMATION_EMAILS_GRAPH_DATA.LOAD });
  getWorkflowEmailsGraphService(data)
    .then((res) => {
      if (res.data.success) {
        const data = res.data.data;
        store.dispatch({ type: AUTOMATION_EMAILS_GRAPH_DATA.SET_DATA, payload: { data } });
      }
    })
    .catch((err) => {
      console.log(err);
      store.dispatch({ type: AUTOMATION_EMAILS_GRAPH_DATA.RESET_DATA });
    });
};

export const setWorkflowGraphDate = (value: SelectOption) => {
  const dateObjectValue = getDateObject(value.value);
  store.dispatch({
    type: AUTOMATION_EMAILS_GRAPH_DATA.SET_DATE,
    payload: {
      selectedDateFilter: { label: value.label, value: value.value },
      selectedDateValue: dateObjectValue,
    },
  });
};

export const resetWorkflowGraphData = () => {
  store.dispatch({
    type: AUTOMATION_EMAILS_GRAPH_DATA.RESET_DATA,
  });
};
export const resetWorkflowGraphDate = () => {
  store.dispatch({
    type: AUTOMATION_EMAILS_GRAPH_DATA.RESET_DATE,
  });
};

export const updateAnEmailInEmailListingAnalytics = (emailId: number, keyToUpdate: string, updatedValue: string) => {
  if (
    store.getState().automation.emailAnalytics.emailsCount &&
    store.getState().automation.emailAnalytics.data.some((email) => email.version_id === emailId)
  ) {
    const emailAnalytics = [...store.getState().automation.emailAnalytics.data];
    const existingEmailIndex = emailAnalytics.findIndex((email) => email.version_id === emailId);

    if (existingEmailIndex !== -1) {
      const updatedEmail = { ...emailAnalytics[existingEmailIndex] };
      updatedEmail[keyToUpdate] = updatedValue;
      emailAnalytics[existingEmailIndex] = updatedEmail;
      store.dispatch({
        type: AUTOMATION_EMAILS_ANALYTICS_LIST.SET_DATA,
        payload: { emails: emailAnalytics, emailsCount: store.getState().automation.emailAnalytics.emailsCount },
      });
    }
  }
};

export const resetWorkflowEmailsAnalytics = () => {
  store.dispatch({ type: AUTOMATION_EMAILS_ANALYTICS_LIST.RESET_DATA });
};

export const updateListSearchTermInStore = (value: string) => {
  store.dispatch({
    type: AUTOMATION_LISTS.SET_SEARCH_TERM,
    payload: value,
  });
  store.dispatch({ type: AUTOMATION_LISTS.RESET_SEARCH_DATA });
};

export const updateSaveButtonState = (value: boolean) => {
  store.dispatch({
    type: AUTOMATION_WORKFLOW.UPDATE_SAVE_BUTTON_STATE,
    payload: value,
  });
};

export const allEmailsInWorkflowDesignReady = (value: boolean) => {
  store.dispatch({
    type: AUTOMATION_WORKFLOW.UPDATE_ALL_EMAILS_DESIGN_READY,
    payload: value,
  });
};
